import { useRef, useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import getConfig from 'next/config'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { colors } from 'shared/lib'
import { RaffleDetail, RaffleEntryError } from 'shared/types/raffles'
import { FC_STORES } from 'shared/constants/raffleStores'
import { useSubmitRaffleEntryMutation } from 'api/rafflesApi'
import { fullNameValidation, phoneValidation } from 'shared/addressValidations'
import { FormikFloatingLabelField } from 'shared/components/FloatingLabelField'
import { t } from 'localization'
import { ProductDataItem } from 'shared/types/ConsumerSearch'
import { toProductDataItem } from 'layout/navigation/search/utils'
import { getConsumerSearchFilters } from 'shared/utils/search/cs-utils'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { formatRaffleDateTime, formatSizeLabel } from './raffleUtils'

const TP = 'shared.components.Raffles.RaffleEntryDetailsStep'

type RaffleEntryDetailsStepProps = {
  raffle: RaffleDetail
  selectedStore: string
  selectedSize: string
  onSubmitSuccess: () => void
  onTrendingProductsFetched: (products: ProductDataItem[]) => void
}

const validationSchema = Yup.object({
  fullName: fullNameValidation(),
  phoneNumber: phoneValidation(),
})

export const RaffleEntryDetailsStep = ({
  raffle,
  selectedStore,
  selectedSize,
  onSubmitSuccess,
  onTrendingProductsFetched,
}: RaffleEntryDetailsStepProps) => {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [apiError, setApiError] = useState('')
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [submitEntry, { isLoading }] = useSubmitRaffleEntryMutation()

  const { publicRuntimeConfig } = getConfig() || {}
  const siteKey = publicRuntimeConfig?.googleRecaptchaPublicSiteKey || ''
  const { currencyCode, country } = useShoppingRegionContext()

  const store = FC_STORES[selectedStore]

  // Fetch trending products for recommendations
  const getTrendingProducts = useCallback(async () => {
    const consumerSearchTrendingSearchQuery = getConsumerSearchFilters({
      searchState: {},
      cookies: {
        currency: currencyCode,
        country: country,
      },
      queryParams: { sortBy: '5' },
      pageLimit: '8',
    })

    const url =
      '/api/product-search?' + new URLSearchParams(consumerSearchTrendingSearchQuery).toString()

    const { data } = await fetch(url, {
      method: 'GET',
    })
      .then(async (res) => {
        return res.json()
      })
      .catch(() => {
        return {
          data: {},
          error: 'error fetching data',
          total: 0,
        }
      })

    const products = toProductDataItem(data?.productsList || [])
    onTrendingProductsFetched(products)
  }, [onTrendingProductsFetched, currencyCode, country])

  // Fetch trending products on mount (client-side only)
  useEffect(() => {
    void getTrendingProducts()
  }, [getTrendingProducts])

  const handleSubmit = async (values: { fullName: string; phoneNumber: string }) => {
    if (!captchaToken) return
    setApiError('')

    sendTrackingEvent('DRAWING_ENTRY_SUBMIT', {
      product_template_slug: raffle.slug,
      store_name: selectedStore,
      size: selectedSize,
    })

    try {
      await submitEntry({
        raffleSlug: raffle.slug,
        storeId: selectedStore,
        size: selectedSize,
        fullName: values.fullName,
        phoneNumber: values.phoneNumber,
        captchaToken,
      }).unwrap()
      sendTrackingEvent('DRAWING_ENTRY_SUCCESS', {
        product_name: raffle.productName,
        product_template_slug: raffle.slug,
        store_name: selectedStore,
        size: selectedSize,
      })
      onSubmitSuccess()
    } catch (error) {
      const err = error as { data?: RaffleEntryError }
      setApiError(
        err?.data?.message ||
          t(`${TP}.submitError`, 'Something went wrong. Please try again.'),
      )
      recaptchaRef.current?.reset()
      setCaptchaToken(null)
    }
  }

  return (
    <Wrapper data-qa="raffle-entry-details-step">
      <Formik
        initialValues={{ fullName: '', phoneNumber: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty }) => (
          <StyledForm>
            <ProductSummary>
              <ProductThumb src={raffle.mainImage.src} alt={raffle.productName} />
              <ProductInfo>
                <ProductTopGroup>
                  <ProductName>{raffle.productName}</ProductName>
                  <ProductDetail>
                    {t(`${TP}.rafflePrice`, 'Price')}: ${raffle.rafflePrice}
                  </ProductDetail>
                  <ProductDetail>
                    {t(`${TP}.closes`, 'Closes')}: {formatRaffleDateTime(raffle.endDate)}
                  </ProductDetail>
                </ProductTopGroup>
                <ProductBottomGroup>
                  <ProductDetail>
                    {t(`${TP}.pickupFrom`, 'Pickup from')}: {store?.name || selectedStore}
                  </ProductDetail>
                  <ProductDetail>
                    {t(`${TP}.size`, 'Size')}: {formatSizeLabel(raffle.sizeUnit, raffle.gender)} {selectedSize}
                  </ProductDetail>
                </ProductBottomGroup>
              </ProductInfo>
            </ProductSummary>

            <ScrollContent>
              <SectionTitle>
                {t(`${TP}.pickupInfo`, 'CONTACT INFORMATION')}
              </SectionTitle>
              <IdNotice>
                {t(
                  `${TP}.idNotice`,
                  'A valid government ID matching the name provided must be presented at pickup.',
                )}
              </IdNotice>

              <FormikFloatingLabelField
                id="raffle-entry-fullname"
                name="fullName"
                label={t(`${TP}.fullName`, 'Full Name')}
                type="text"
                autoComplete="name"
                qaAttr="raffle-entry-fullname"
              />

              <FormikFloatingLabelField
                id="raffle-entry-phone"
                name="phoneNumber"
                label={t(`${TP}.phoneNumber`, 'Phone Number')}
                type="tel"
                autoComplete="tel"
                qaAttr="raffle-entry-phone"
              />

              <PaymentSection>
                <SectionTitle>{t(`${TP}.payment`, 'PAYMENT DETAILS')}</SectionTitle>
                <PaymentText>
                  Payment plus applicable taxes will be collected in-store upon pickup.
                </PaymentText>
              </PaymentSection>

              {siteKey && (
                <CaptchaWrapper>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={siteKey}
                    onChange={(token) => setCaptchaToken(token)}
                  />
                </CaptchaWrapper>
              )}

              {apiError && (
                <ErrorBanner data-qa="raffle-entry-error">{apiError}</ErrorBanner>
              )}

              <TermsText>
                By proceeding, you confirm you have read and agree to the{' '}
                <TermsLink
                  href="https://support.flightclub.com/hc/en-us/articles/45796369528461-FLIGHT-CLUB-DRAWINGS-OFFICIAL-RULES"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </TermsLink>
                {' '}and understand that, if selected, you must collect your item within the designated pickup window or your entry will be forfeited.
              </TermsText>
            </ScrollContent>

            <Footer>
              <SubmitButton
                type="submit"
                disabled={!isValid || !dirty || !captchaToken || isLoading}
                data-qa="raffle-entry-submit"
              >
                {isLoading
                  ? t(`${TP}.submitting`, 'SUBMITTING...')
                  : t(`${TP}.submitEntry`, 'SUBMIT ENTRY')}
              </SubmitButton>
            </Footer>
          </StyledForm>
        )}
      </Formik>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`

const SectionTitle = styled.h3`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
  margin: 0;
`

const IdNotice = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: ${colors.FC2_GREY};
  margin: 0 0 8px;
`

const PaymentSection = styled.div`
  margin-top: 12px;
  margin-left: -24px;
  margin-right: -24px;
  padding-top: 24px;
  padding-left: 24px;
  padding-right: 24px;
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PaymentText = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: ${colors.FC2_GREY};
  margin: 0;
`

const CaptchaWrapper = styled.div`
  margin: 8px 0;
`

const ErrorBanner = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: ${colors.FC2_RED};
  padding: 12px;
  border: 1px solid ${colors.FC2_RED};
  border-radius: 2px;
`

const Footer = styled.div`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 16px 24px 24px;
  flex-shrink: 0;
  background: ${colors.FC2_WHITE};
`

const ProductSummary = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const ProductThumb = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  flex-shrink: 0;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const ProductTopGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ProductBottomGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ProductName = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.FC2_BLACK};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ProductDetail = styled.span`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.FC2_GREY};
  line-height: 1.3;
`

const TermsLink = styled.a`
  color: ${colors.FC2_GREY};
  text-decoration: underline;
`

const TermsText = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: ${colors.FC2_GREY};
  font-style: italic;
  margin: 8px 0 0;
`

const SubmitButton = styled.button`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  width: 100%;
  height: 56px;
  background-color: ${colors.FC2_RED};
  color: ${colors.FC2_WHITE};
  border: none;
  border-radius: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:disabled {
    background-color: ${colors.FC2_DISABLED_GREY};
    pointer-events: none;
  }
`
